import cookie from 'cookie';
import 'isomorphic-fetch';
import queryString from 'query-string';
import config from 'config';

import { logout } from 'actions';

function callApi(endpoint, method, data, queryParams, token, options) {
  let fullUrl = options.apiRoot + endpoint;
  let requestOptions = {
    method: method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    }
  };

  if (options.csrfToken) {
    requestOptions.headers['X-CSRFToken'] = options.csrfToken;
  }

  if (queryParams && typeof queryParams === 'object') {
    fullUrl += '?' + queryString.stringify(queryParams);
  }

  if (data) {
    requestOptions.headers['Content-Type'] = 'application/json';
    requestOptions.body = JSON.stringify(data);
  }

  if (token) {
    requestOptions.headers['X-authorization'] = `JWT ${token}`;
  }

  return fetch(fullUrl, requestOptions)
    .then(response => {
      const { status, ok } = response;

      if (ok && (method === 'DELETE' || status === 204)) {
        return { status };
      }

      return response.json().then(json => {
        if (!ok) {
          // eslint-disable-next-line
          throw { json, status };
        } else {
          return { json, status };
        }
      });
    });
}

function uploadFiles(endpoint, method, data, files, field, token, options) {
  if (!files) {
    return;
  }

  let fullUrl = options.apiRoot + endpoint;
  let requestOptions = {
    method: method || 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRFToken': options.csrfToken
    }
  };

  if (options.cookie) {
    requestOptions.headers.Cookie = options.cookie;
  }

  if (token) {
    requestOptions.headers['X-authorization'] = `JWT ${token}`;
  }

  if (!field) {
    field = 'photo';
  }

  return Promise.all(files.map((file) => {
    let formData = new FormData();
    formData.append(field, file, file.filename);

    if (data) {
      Object.keys(data).forEach(key => formData.append(key, data[key]));
    }

    let options = Object.assign({}, requestOptions, { body: formData });

    return fetch(fullUrl, options).then(response => {
      const { status, ok, headers } = response;
      const result = { status };

      let promise;
      if (headers.get('Content-Type') && headers.get('Content-Type').indexOf('application/json') !== -1) {
        promise = response.json().then(json => {
          result.json = json;
        });

      } else {
        promise = response.text().then(text => {
          result.text = text;
        });
      }

      return promise.then(() => {
        if (!ok) {
          throw result;
        } else {
          return result;
        }
      });
    });
  }));
}

function makeOptions(req, store, apiRoot) {
  const protocol = req ? config.server.apiProtocol + '://' : '//';
  const hostname = req ? config.server.apiHost : window.location.hostname;
  let port;
  let apiPath;
  let csrfToken;
  let cookies;
  let headers = {};

  if (req) {
    port = (config.server.apiPort) ? `:${config.server.apiPort}` : '';
    apiPath = (apiRoot) ? apiRoot : config.apiRoot;
    cookies = req.cookies;
    headers = req.headers;

  } else if (typeof window !== 'undefined' && window.location) {
    port = (window.location.port) ? `:${window.location.port}` : '';
    apiPath = (apiRoot) ? apiRoot : config.apiRoot;

    if (cookie.parse(document.cookie).csrftoken) {
      csrfToken = cookie.parse(document.cookie).csrftoken;
    }

    const priceType = cookie.parse(document.cookie).priceType;

    if (priceType) {
      cookies = { priceType };
    }
  } else {
    return null;
  }

  return {
    apiRoot: `${protocol}${hostname}${port}${apiPath}`,
    csrfToken,
    cookies,
    headers
  };
}

function actionWith(action, status, obj) {
  if (!obj) {
    obj = {};
  }

  return Object.assign({}, action, { status: status }, obj);
}

export default function createApiMiddleware(req) {
  return store => next => action => {
    if (!action) {
      return;
    }

    if (typeof action.meta === 'undefined') {
      return next(action);
    }

    const { meta } = action;
    const { method, endpoint, data, files, field, queryParams } = meta;
    const { apiRoot } = meta;

    const options = makeOptions(req, store, apiRoot);
    const token = store.getState().auth ? store.getState().auth.token : null;

    const successCallback = data => {
      let { json, status } = data;

      if (Array.isArray(data)) {
        json = data.map(el => el.json);
        status = data.map(el => el.status);
      }

      return next(actionWith(action, 'SUCCESS', {
        response: json,
        statusCode: status
      }));
    };

    const failCallback = (data) => {
      let { json, text, status } = data;

      if (Array.isArray(data)) {
        json = data.map(el => el.json);
        status = data.map(el => el.status);
      }

      if (status === 401 || (Array.isArray(status) && status.indexOf(401) > -1)) {
        next(logout());
      }

      return next(actionWith(action, 'FAIL', {
        error: json || text || 'Something bad happened',
        statusCode: status
      }));
    };

    if (!options) {
      throw Error('Can\'t send API request');
    }

    next(actionWith(action, 'SEND'));

    if (files) {
      return uploadFiles(endpoint, method, data, files, field, token, options).then(successCallback, failCallback);
    }

    return callApi(endpoint, method, data, queryParams, token, options).then(successCallback, failCallback);
  };
}



// WEBPACK FOOTER //
// ./src/middleware/api.js